import { Box } from '@mui/material';
import {
  BuildCartEntity,
  BuildCartPhase,
  caseScheduleKeys,
  caseScheduleRepository,
  ChecklistItemEntity,
  computeBuildStatus,
  PickingStationEntity,
  SurgicalCaseEntity,
} from '@peritasai/repository';
import { CaseBuildStatus } from '@peritasai/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  redirect,
  useBlocker,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BuildCartHeader,
  CartRemovalDialog,
  CartRemovalSuccessDialog,
  DetectionComponent,
  ItemDetectionStatusComponent,
  LeaveSessionDialog,
  SessionEndingDialog,
} from '../../../features/case-schedule/build-cart/components';
import { useCaseEditingSession } from '../../../features/case-schedule/hooks';
import { Permission } from '../../../features/auth';
import { PickingStationSelectionDialog } from '../../../shared';

const IDLE_TIMEOUT_MS = 1 * 60 * 1000; //TODO: upate after testing to 4 minutes
const WARNING_COUNTDOWN_SECONDS = 60; //TODO: upate after testing to 60 seconds

export const Route = createFileRoute('/_auth/case-schedule/build-cart')({
  staticData: {
    title: 'pages.buildCart.title',
    description: 'pages.buildCart.description',
  },
  validateSearch: (search: Record<string, unknown>) => ({
    caseId: (search.caseId as string | undefined) ?? undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const { isAuthenticated, hasPermission, isAdmin } = context.auth;
    if (!isAuthenticated) throw redirect({ to: '/' });
    if (!hasPermission(Permission.CART_BUILDER) && !isAdmin()) throw redirect({ to: '/403' });
    const caseId = search.caseId as string | undefined;
    if (!caseId) throw redirect({ to: '/case-schedule' });
  },
  component: BuildCartPage,
});

function BuildCartPage() {
  const navigate = useNavigate();
  const { caseId } = useSearch({ from: '/_auth/case-schedule/build-cart' });

  // Build Cart state
  const [buildCartEntity, setBuildCartEntity] =
    useState<BuildCartEntity | null>(null);
  const [caseEntity, setCaseEntity] = useState<SurgicalCaseEntity | null>(null);
  const [isUpdatingCart, setIsUpdatingCart] = useState(false);
  const [isUpdatingTray, setIsUpdatingTray] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isSavingAndClosing, setIsSavingAndClosing] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  // Detection state
  const [detectionState, setDetectionState] = useState<
    'info' | 'detected' | 'error-assigned' | 'error-wrong-cart'
  >('info');
  const [detectionPhase, setDetectionPhase] = useState<BuildCartPhase>(BuildCartPhase.CART);
  const [pickingStation, setPickingStation] =
    useState<string>('Picking Station 1');
  const [detectedItem, setDetectedItem] = useState<
    { id: string; name: string } | undefined
  >();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [errorType, setErrorType] = useState<'assigned' | 'wrong-cart' | null>(
    null
  );
  const [assignedCartName, setAssignedCartName] = useState<
    string | undefined
  >();

  // Picking Station state
  const [pickingStations, setPickingStations] = useState<
    PickingStationEntity[] | null
  >(null);
  const [selectedPickingStationId, setSelectedPickingStationId] = useState<
    string | null
  >(null);
  const selectedPickingStationIdRef = useRef<string | null>(null);
  const [showPickingStationDialog, setShowPickingStationDialog] =
    useState(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showSessionEndingModal, setShowSessionEndingModal] = useState(false);
  const [hasSessionTimedOut, setHasSessionTimedOut] = useState(false);

  // Cart removal state
  const [showCartRemovalDialog, setShowCartRemovalDialog] = useState(false);
  const [showCartRemovalSuccess, setShowCartRemovalSuccess] = useState(false);
  const cartRemovalTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const latestCaseSnapshotRef = useRef<SurgicalCaseEntity | null>(null);
  const updateCaseEntity = useCallback((entity: SurgicalCaseEntity | null) => {
    setCaseEntity(entity);
    if (entity) {
      latestCaseSnapshotRef.current = entity;
    }
  }, []);

  const queryClient = useQueryClient();

  // Helper function to invalidate case schedule queries
  // This ensures fresh data is loaded when returning from build-cart
  // even if SSE events were missed while the stream was closed
  const invalidateCaseScheduleQueries = useCallback(() => {
    // Invalidate all case list queries
    queryClient.invalidateQueries({
      queryKey: caseScheduleKeys.lists(),
    });
    // Invalidate all workflow summary queries
    queryClient.invalidateQueries({
      queryKey: caseScheduleKeys.all,
    });
  }, [queryClient]);

  // Calculate the appropriate buildStatus based on tray availability and allocation
  const calculateBuildStatus = useCallback((): CaseBuildStatus | null => {
    const buildChecklist = latestCaseSnapshotRef.current?.buildChecklist;
    if (!buildChecklist) return null;

    return computeBuildStatus(buildChecklist);
  }, []);

  const finalizeBuildStatus = useCallback(async () => {
    if (!caseId) return;
    const nextStatus = calculateBuildStatus();
    if (!nextStatus) return;

    try {
      await caseScheduleRepository.updateBuildStatus(caseId, nextStatus);
    } catch (error) {
      console.error('Failed to finalize build status:', error);
    }
  }, [caseId, calculateBuildStatus]);

  const handleSessionStart = useCallback(async () => {
    if (!caseId) return;

    try {
      await caseScheduleRepository.updateBuildStatus(
        caseId,
        CaseBuildStatus.IN_PROGRESS
      );
    } catch (error) {
      console.error(
        'Failed to set build status to IN_PROGRESS on session start:',
        error
      );
      // Don't throw - session start should succeed even if buildStatus update fails
    }
  }, [caseId]);

  // Helper function to mark sidehub as available
  const markSidehubAsAvailable = useCallback(async () => {
    const sidehubId = selectedPickingStationIdRef.current;
    if (!sidehubId) {
      return;
    }

    try {
      await caseScheduleRepository.updateSidehubAvailableStatus(
        sidehubId,
        true
      );
    } catch (error) {
      console.error('Failed to mark sidehub as available:', error);
      // Fire-and-forget - don't block navigation
    }
  }, []);

  const handleSessionEnd = useCallback(async () => {
    const tasks = [
      { name: 'finalizeBuildStatus', promise: finalizeBuildStatus() },
      { name: 'markSidehubAsAvailable', promise: markSidehubAsAvailable() }
    ];

    const results = await Promise.allSettled(tasks.map(t => t.promise));

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`Failed to ${tasks[index].name}:`, result.reason);
      }
    });
  }, [finalizeBuildStatus, markSidehubAsAvailable]);

  const {
    session,
    isStarting,
    error: sessionError,
    isLockedByAnotherUser,
    stopSession,
  } = useCaseEditingSession(caseId, true, {
    onSessionEnd: handleSessionEnd,
    onSessionStart: handleSessionStart,
  });
  const isSessionActive = !!session;

  const leaveBlocker = useBlocker({
    shouldBlockFn: () => !!session && !isDispatching && !hasSessionTimedOut,
    withResolver: true,
    enableBeforeUnload: false,
  });

  useEffect(() => {
    if (leaveBlocker.status === 'blocked' && !hasSessionTimedOut) {
      setShowLeaveModal(true);
    } else if (hasSessionTimedOut) {
      // If session timed out, don't show leave modal and reset blocker
      setShowLeaveModal(false);
      leaveBlocker.reset?.();
    }
  }, [leaveBlocker.status, hasSessionTimedOut, leaveBlocker]);

  const handleLeaveCancel = useCallback(() => {
    leaveBlocker.reset?.();
    setShowLeaveModal(false);
  }, [leaveBlocker]);

  const handleLeaveConfirm = useCallback(async () => {
    setIsLeaving(true);
    try {
      await stopSession();
      invalidateCaseScheduleQueries();
      leaveBlocker.proceed?.();
    } catch (error) {
      console.error('Failed to end session before leaving', error);
      leaveBlocker.reset?.();
    } finally {
      setIsLeaving(false);
      setShowLeaveModal(false);
    }
  }, [
    leaveBlocker,
    stopSession,
    invalidateCaseScheduleQueries,
  ]);

  // Fetch picking stations when session starts
  useEffect(() => {
    if (!session || isStarting) {
      return;
    }

    const fetchPickingStations = async () => {
      try {
        const stations = await caseScheduleRepository.getPickingStations();
        setPickingStations(stations);

        // Show dialog if no station is selected yet
        if (!selectedPickingStationId) {
          setShowPickingStationDialog(true);
        }
      } catch (error) {
        console.error('Failed to fetch picking stations:', error);
      }
    };

    fetchPickingStations();
  }, [session, isStarting, selectedPickingStationId]);

  // Poll picking station status when dialog is open
  useEffect(() => {
    if (!showPickingStationDialog || !session) {
      // Clear polling when dialog is closed
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Poll every 5 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const stations = await caseScheduleRepository.getPickingStations();
        setPickingStations(stations);
      } catch (error) {
        console.error('Failed to poll picking stations:', error);
      }
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [showPickingStationDialog, session]);

  // Initialize build cart when caseId is provided
  useEffect(() => {
    if (!caseId) return;

    const loadCaseData = async () => {
      try {
        const caseData = await caseScheduleRepository.getCase(caseId);
        updateCaseEntity(caseData);

        if (caseData.buildChecklist) {
          // Always start with cart phase regardless of cart assignment status
          const initialPhase: BuildCartPhase = BuildCartPhase.CART;

          const buildCartDto = {
            caseId: caseData.id,
            currentPhase: initialPhase,
            selectedCartId: caseData.cartID || undefined,
            selectedCartLocation: caseData.cartLocation || undefined,
            selectedCartReferenceNumber:
              caseData.cartReferenceNumber || undefined,
            traysLoaded: caseData.traysLoaded || 0,
            traysRequired: caseData.traysRequired || 0,
            instrumentsSuppliesLoaded: caseData.instrumentsSuppliesLoaded || 0,
            instrumentsSuppliesRequired:
              caseData.instrumentsSuppliesRequired || 0,
          };
          setBuildCartEntity(new BuildCartEntity(buildCartDto));
        }
      } catch (error) {
        console.error('Failed to load case data:', error);
      }
    };

    loadCaseData();
  }, [caseId, updateCaseEntity]);

  // Determine current phase based on buildCartEntity state
  const currentPhase: BuildCartPhase = useMemo(() => {
    if (!buildCartEntity) return BuildCartPhase.CART;
    // Respect the currentPhase from buildCartEntity instead of auto-switching
    return buildCartEntity.currentPhase;
  }, [buildCartEntity]);

  // Helper function to get assigned cart name
  const getAssignedCartName = useCallback((): string => {
    if (buildCartEntity?.selectedCartReferenceNumber) {
      return buildCartEntity.selectedCartReferenceNumber;
    }
    if (
      caseEntity?.preferenceCard?.trackedAssets.caseCarts &&
      caseEntity.preferenceCard.trackedAssets.caseCarts.length > 0
    ) {
      return caseEntity.preferenceCard.trackedAssets.caseCarts[0].name;
    }
    return '';
  }, [buildCartEntity, caseEntity]);

  // Set assignedCartName when cart is assigned (for display in info state)
  useEffect(() => {
    const cartId = buildCartEntity?.selectedCartId || caseEntity?.cartID;
    if (cartId) {
      // Cart is assigned, use the reference number if available, otherwise get from preference card
      if (buildCartEntity?.selectedCartReferenceNumber) {
        setAssignedCartName(buildCartEntity.selectedCartReferenceNumber);
      } else if (
        caseEntity?.preferenceCard?.trackedAssets.caseCarts &&
        caseEntity.preferenceCard.trackedAssets.caseCarts.length > 0
      ) {
        // Fallback to preference card cart name if reference number not available
        setAssignedCartName(
          caseEntity.preferenceCard.trackedAssets.caseCarts[0].name
        );
      }
    } else {
      // No cart assigned, clear assignedCartName
      setAssignedCartName(undefined);
    }
  }, [
    buildCartEntity?.selectedCartId,
    buildCartEntity?.selectedCartReferenceNumber,
    caseEntity?.cartID,
    caseEntity?.preferenceCard?.trackedAssets.caseCarts,
  ]);

  // Handle cart selection
  const handleCartSelect = useCallback(
    async (checklistItem: ChecklistItemEntity, isCorrectCart = true) => {
      if (!caseId || !buildCartEntity) return;
      setIsUpdatingCart(true);
      setDetectionState('info'); // Reset to info while processing
      setErrorType(null);
      try {
        const updatedCase = await caseScheduleRepository.addTrackedItem(
          caseId,
          checklistItem,
          isCorrectCart,
          BuildCartPhase.CART
        );
        updateCaseEntity(updatedCase);
        if (updatedCase.buildChecklist) {
          const newEntity = new BuildCartEntity({
            caseId: buildCartEntity.caseId,
            currentPhase: BuildCartPhase.TRAY, // Move to tray phase after cart is selected
            selectedCartId: updatedCase.cartID || undefined,
            selectedCartLocation: updatedCase.cartLocation || undefined,
            selectedCartReferenceNumber:
              updatedCase.cartReferenceNumber || undefined,
            traysLoaded: updatedCase.traysLoaded || 0,
            traysRequired: updatedCase.traysRequired || 0,
            instrumentsSuppliesLoaded:
              updatedCase.instrumentsSuppliesLoaded || 0,
            instrumentsSuppliesRequired:
              updatedCase.instrumentsSuppliesRequired || 0,
          });
          setBuildCartEntity(newEntity);

          // Update detection state to success
          if (updatedCase.cartID) {
            // Prioritize cart reference number (matches info message), then fallback to preference card name
            let cartName =
              updatedCase.cartReferenceNumber ||
              updatedCase.cartLocation ||
              'Cart';
            if (
              !updatedCase.cartReferenceNumber &&
              updatedCase.preferenceCard
            ) {
              const cartPreferenceItem =
                updatedCase.preferenceCard.trackedAssets.caseCarts.find(
                  (cart) => cart.catalogItemId === checklistItem.catalogItemId
                );
              if (cartPreferenceItem?.name) {
                cartName = cartPreferenceItem.name;
              }
            }

            setDetectionPhase(BuildCartPhase.CART);
            setDetectionState('detected');
            setDetectedItem({
              id: updatedCase.cartID,
              name: cartName,
            });
          }
        }
      } catch (error) {
        console.error('Failed to update cart selection:', error);

        const err = error as {
          errorType?: string;
          wrongCartName?: string;
          code?: string;
        };
        // Check if error has error type information
        const errorTypeFromError = err?.errorType;
        const wrongCartName = err?.wrongCartName;
        const errorCode = err?.code;

        if (errorCode) {
          let cartName =
            checklistItem.assetId || checklistItem.catalogItemId || 'Cart';
          if (caseEntity?.preferenceCard) {
            const cartPreferenceItem =
              caseEntity.preferenceCard.trackedAssets.caseCarts.find(
                (cart) => cart.catalogItemId === checklistItem.catalogItemId
              );
            if (cartPreferenceItem?.name) {
              cartName = cartPreferenceItem.name;
            }
          }

          setDetectedItem({
            id: checklistItem.assetId || checklistItem.catalogItemId || '',
            name: cartName,
          });
          setErrorType('assigned');
          setDetectionState('error-assigned');

          switch (errorCode) {
            case 'asset-id-required':
              setErrorMessage('No cart detected. Please scan a cart.');
              break;
            case 'cart-asset-type-mismatch':
              setErrorMessage('Selected asset is not a valid case cart.');
              break;
            case 'cart-already-allocated-to-other-case':
              setErrorMessage('Cart is already allocated to another case.');
              break;
            case 'asset-already-allocated-to-other-case':
              setErrorMessage('Asset is already allocated to another case.');
              break;
            case 'asset-allocation-state-inconsistent':
              setErrorMessage(
                'Cart assignment state is inconsistent. Please retry.'
              );
              break;
            default:
              setErrorMessage('Failed to assign cart. Please try again.');
          }
        } else if (
          errorTypeFromError === 'wrong-cart-detected' ||
          isCorrectCart === false
        ) {
          // Only treat as wrong cart error if:
          // 1. Error type is explicitly 'wrong-cart-detected', OR
          // 2. isCorrectCart is explicitly false (wrong cart simulation)
          // Wrong cart detected error
          // Wrong cart detected error
          const assignedCartNameValue = getAssignedCartName();

          setErrorType('wrong-cart');
          setDetectionState('error-wrong-cart');
          setAssignedCartName(assignedCartNameValue);
          // Store only the detected wrong cart name in detectedItem
          setDetectedItem({
            id: wrongCartName || 'CC-ORTHO-002',
            name: wrongCartName || 'CC-ORTHO-002',
          });
        } else if (errorTypeFromError === 'no-available-cart') {
          // No available cart error - this should show as "no cart available" not wrong cart
          // For now, treat as assigned error since it's not a wrong cart issue
          let cartName =
            checklistItem.assetId || checklistItem.catalogItemId || 'Cart';
          if (caseEntity?.preferenceCard) {
            const cartPreferenceItem =
              caseEntity.preferenceCard.trackedAssets.caseCarts.find(
                (cart) => cart.catalogItemId === checklistItem.catalogItemId
              );
            if (cartPreferenceItem?.name) {
              cartName = cartPreferenceItem.name;
            }
          }

          setDetectedItem({
            id: checklistItem.assetId || checklistItem.catalogItemId || '',
            name: cartName,
          });
          setErrorType('assigned');
          setDetectionState('error-assigned');
          setErrorMessage('No available cart found.');
        } else {
          // Cart assigned to different case error (existing behavior)
          // This is the default case for any other error when isCorrectCart is true
          let cartName =
            checklistItem.assetId || checklistItem.catalogItemId || 'Cart';
          if (caseEntity?.preferenceCard) {
            const cartPreferenceItem =
              caseEntity.preferenceCard.trackedAssets.caseCarts.find(
                (cart) => cart.catalogItemId === checklistItem.catalogItemId
              );
            if (cartPreferenceItem?.name) {
              cartName = cartPreferenceItem.name;
            }
          }

          setDetectedItem({
            id: checklistItem.assetId || checklistItem.catalogItemId || '',
            name: cartName,
          });
          setErrorType('assigned');
          setDetectionState('error-assigned');
          setErrorMessage('Failed to assign cart. Please try again.');
        }
      } finally {
        setIsUpdatingCart(false);
      }
    },
    [caseId, buildCartEntity, updateCaseEntity, caseEntity]
  );

  // Handle tray selection
  const handleTraySelect = useCallback(
    async (checklistItem: ChecklistItemEntity) => {
      if (!caseId || !buildCartEntity) return;
      setIsUpdatingTray(true);
      setDetectionState('info'); // Reset to info while processing
      try {
        const updatedCase = await caseScheduleRepository.addTrackedItem(
          caseId,
          checklistItem,
          true,
          BuildCartPhase.TRAY
        );
        updateCaseEntity(updatedCase);
        if (updatedCase.buildChecklist) {
          const newEntity = new BuildCartEntity({
            caseId: buildCartEntity.caseId,
            currentPhase: buildCartEntity.currentPhase,
            selectedCartId:
              updatedCase.cartID || buildCartEntity.selectedCartId,
            selectedCartLocation:
              updatedCase.cartLocation || buildCartEntity.selectedCartLocation,
            selectedCartReferenceNumber:
              updatedCase.cartReferenceNumber ||
              buildCartEntity.selectedCartReferenceNumber,
            traysLoaded: updatedCase.buildChecklist.traysAllocated || 0,
            traysRequired: updatedCase.buildChecklist.traysTotal || 0,
            instrumentsSuppliesLoaded:
              updatedCase.instrumentsSuppliesLoaded || 0,
            instrumentsSuppliesRequired:
              updatedCase.instrumentsSuppliesRequired || 0,
          });
          setBuildCartEntity(newEntity);

          // Update detection state to success
          // Find the updated checklist item from the updated case to get the assetId
          const updatedTrays = updatedCase.buildChecklist.getTrays();
          let updatedChecklistItem: ChecklistItemEntity | undefined;
          for (const trayGroup of updatedTrays) {
            updatedChecklistItem = trayGroup.items.find(
              (item) => item.checklistItemId === checklistItem.checklistItemId
            );
            if (updatedChecklistItem) break;
          }

          // Always show success if API call succeeded (no error thrown)
          // Try to get assetId from the updated item, or find any allocated tray
          let assetId: string | undefined;
          let catalogItemId: string = checklistItem.catalogItemId;

          if (updatedChecklistItem?.assetId) {
            assetId = updatedChecklistItem.assetId;
          } else {
            // Fallback: find any newly allocated tray item with assetId
            for (const trayGroup of updatedTrays) {
              const allocatedItem = trayGroup.items.find(
                (item) => item.allocated && item.assetId
              );
              if (allocatedItem?.assetId) {
                assetId = allocatedItem.assetId;
                catalogItemId = trayGroup.catalogItemId;
                break;
              }
            }
          }

          if (assetId) {
            // Look up tray name from preference card
            let trayName = assetId;
            if (updatedCase.preferenceCard) {
              const trayPreferenceItem =
                updatedCase.preferenceCard.trackedAssets.trays.find(
                  (tray) => tray.catalogItemId === catalogItemId
                );
              if (trayPreferenceItem?.name) {
                trayName = trayPreferenceItem.name;
              }
            }

            setDetectionPhase(BuildCartPhase.TRAY);
            setDetectionState('detected');
            setDetectedItem({
              id: assetId,
              name: trayName,
            });
          }
        }
      } catch (error) {
        console.error('Failed to update tray selection:', error);
        const err = error as { code?: string };
        const errorCode = err?.code;

        // Look up tray name from preference card for error display
        let trayName =
          checklistItem.assetId || checklistItem.catalogItemId || 'Tray';
        if (caseEntity?.preferenceCard) {
          const trayPreferenceItem =
            caseEntity.preferenceCard.trackedAssets.trays.find(
              (tray) => tray.catalogItemId === checklistItem.catalogItemId
            );
          if (trayPreferenceItem?.name) {
            trayName = trayPreferenceItem.name;
          }
        }

        setDetectedItem({
          id: checklistItem.assetId || checklistItem.catalogItemId || '',
          name: trayName,
        });
        setErrorType('assigned');
        setDetectionState('error-assigned');
        if (errorCode === 'asset-id-required') {
          setErrorMessage('No tray detected. Please scan a tray.');
        } else if (errorCode === 'tray-already-allocated-to-other-case') {
          setErrorMessage('Tray is already allocated to another case.');
        } else if (errorCode === 'asset-already-allocated-to-other-case') {
          setErrorMessage('Asset is already allocated to another case.');
        } else if (errorCode === 'asset-allocation-state-inconsistent') {
          setErrorMessage(
            'Tray assignment state is inconsistent. Please retry.'
          );
        } else {
          setErrorMessage('Failed to detect tray. Please try again.');
        }
      } finally {
        setIsUpdatingTray(false);
      }
    },
    [caseId, buildCartEntity, updateCaseEntity, caseEntity]
  );


  const handleBuildCartNext = useCallback(async () => {
    if (!buildCartEntity || !caseId || !caseEntity) return;

    setIsDispatching(true);
    try {
      // Update buildStatus before dispatching
      const nextStatus = calculateBuildStatus();
      if (nextStatus) {
        await caseScheduleRepository.updateBuildStatus(caseId, nextStatus);
      }

      // Dispatch the case
      await caseScheduleRepository.updateDispatchStatus(caseId, true);
      // After successful dispatch, stop the session (cleanup handled by handleSessionEnd)
      await stopSession();
      invalidateCaseScheduleQueries();
      navigate({ to: '/case-schedule' });
    } catch (error) {
      console.error('Failed to dispatch case:', error);
      // TODO: Show error message to user
    } finally {
      setIsDispatching(false);
    }
  }, [
    buildCartEntity,
    caseId,
    caseEntity,
    navigate,
    stopSession,
    calculateBuildStatus,
    invalidateCaseScheduleQueries,
  ]);

  // Handler for picking station selection
  // Handler for changing selected station (preview only, doesn't confirm)
  const handlePickingStationChange = useCallback((stationId: string | null) => {
    setSelectedPickingStationId(stationId);
    // Don't update ref here - only update on confirmation
  }, []);

  // Handler for confirming station selection
  const handlePickingStationSelect = useCallback(
    async (stationId: string) => {
      const station = pickingStations?.find((s) => s.id === stationId);
      if (station) {
        setSelectedPickingStationId(stationId);
        selectedPickingStationIdRef.current = stationId;
        setPickingStation(station.name);
        setShowPickingStationDialog(false);

        // Mark sidehub as unavailable
        try {
          await caseScheduleRepository.updateSidehubAvailableStatus(
            stationId,
            false
          );
        } catch (error) {
          console.error('Failed to mark sidehub as unavailable:', error);
          // Don't block selection - allow user to continue
        }
      }
    },
    [pickingStations]
  );

  // Handler for picking station dialog cancel
  const handlePickingStationCancel = useCallback(async () => {
    setShowPickingStationDialog(false);
    await stopSession();
    invalidateCaseScheduleQueries();
    navigate({ to: '/case-schedule' });
  }, [
    navigate,
    stopSession,
    invalidateCaseScheduleQueries,
  ]);

  // Handler for picking station edit (reopen dialog)
  const handlePickingStationEdit = useCallback(() => {
    setShowPickingStationDialog(true);
  }, []);

  // Handler for detection simulation (clicking DetectionComponent)
  const handleDetect = useCallback(
    (isCorrectCart = true) => {
      const phase = currentPhase;
      if (
        phase === BuildCartPhase.CART &&
        caseEntity?.preferenceCard &&
        caseEntity?.buildChecklist
      ) {
        // If wrong cart detected, skip normal flow and call repository directly
        if (!isCorrectCart) {
          const assignedCartNameValue = getAssignedCartName();
          setAssignedCartName(assignedCartNameValue);

          // Find the first cart preference item to use for wrong cart detection
          const carts = caseEntity.preferenceCard.trackedAssets.caseCarts;
          const availableCartGroups =
            caseEntity.buildChecklist.getAvailableCarts();

          if (carts.length === 0 || availableCartGroups.length === 0) {
            setErrorType('wrong-cart');
            setDetectionState('error-wrong-cart');
            setDetectedItem({
              id: 'CC-ORTHO-002',
              name: 'CC-ORTHO-002',
            });
            return;
          }

          // Use first cart item for wrong cart detection simulation
          const firstCart = carts[0];
          const matchingGroup = availableCartGroups.find(
            (group) => group.catalogItemId === firstCart.catalogItemId
          );

          if (!matchingGroup) {
            setErrorType('wrong-cart');
            setDetectionState('error-wrong-cart');
            setDetectedItem({
              id: 'CC-ORTHO-002',
              name: 'CC-ORTHO-002',
            });
            return;
          }

          // Find the first checklist item (allocated or not) for wrong cart detection
          const firstItem = matchingGroup.items[0];
          if (firstItem) {
            handleCartSelect(firstItem, false);
          } else {
            setErrorType('wrong-cart');
            setDetectionState('error-wrong-cart');
            setDetectedItem({
              id: 'CC-ORTHO-002',
              name: 'CC-ORTHO-002',
            });
          }
          return;
        }

        // Normal detection flow (isCorrectCart === true)
        // Check if cart is already assigned
        if (caseEntity.cartID && caseEntity.cartLocation) {
          // Cart is already assigned - show success without API call
          // Prioritize cart reference number (matches info message), then fallback to preference card name
          let cartName =
            caseEntity.cartReferenceNumber || caseEntity.cartLocation || 'Cart';
          if (
            !caseEntity.cartReferenceNumber &&
            caseEntity.preferenceCard &&
            caseEntity.buildChecklist
          ) {
            // Find the cart preference item that matches the assigned cart
            // by checking which cart group contains the allocated item with matching assetId
            const cartGroups = caseEntity.buildChecklist.getAvailableCarts();
            for (const group of cartGroups) {
              const allocatedItem = group.items.find(
                (item) => item.allocated && item.assetId === caseEntity.cartID
              );
              if (allocatedItem) {
                // Found the matching cart group, now find the preference card item
                const cartPreferenceItem =
                  caseEntity.preferenceCard.trackedAssets.caseCarts.find(
                    (cart) => cart.catalogItemId === group.catalogItemId
                  );
                if (cartPreferenceItem?.name) {
                  cartName = cartPreferenceItem.name;
                }
                break;
              }
            }
          }

          // Update detection state to show success
          setDetectionPhase(BuildCartPhase.CART);
          setDetectionState('detected');
          setDetectedItem({
            id: caseEntity.cartID,
            name: cartName,
          });

          // Update buildCartEntity to move to tray phase
          if (buildCartEntity) {
            const newEntity = new BuildCartEntity({
              caseId: buildCartEntity.caseId,
              currentPhase: BuildCartPhase.TRAY,
              selectedCartId: caseEntity.cartID,
              selectedCartLocation: caseEntity.cartLocation,
              selectedCartReferenceNumber: caseEntity.cartReferenceNumber,
              traysLoaded: buildCartEntity.traysLoaded,
              traysRequired: buildCartEntity.traysRequired,
              instrumentsSuppliesLoaded:
                buildCartEntity.instrumentsSuppliesLoaded,
              instrumentsSuppliesRequired:
                buildCartEntity.instrumentsSuppliesRequired,
            });
            setBuildCartEntity(newEntity);
          }
        } else {
          // Cart is not assigned - proceed with normal assignment flow
          // Find the first available cart
          const carts = caseEntity.preferenceCard.trackedAssets.caseCarts;
          const availableCartGroups =
            caseEntity.buildChecklist.getAvailableCarts();

          if (carts.length === 0 || availableCartGroups.length === 0) {
            const assignedCartNameValue = getAssignedCartName();
            setErrorType('wrong-cart');
            setDetectionState('error-wrong-cart');
            setAssignedCartName(assignedCartNameValue);
            setDetectedItem({
              id: 'CC-WRONG-CART',
              name: 'CC-WRONG-CART',
            });
            return;
          }

          // Find the first cart preference item that has an unallocated checklist item
          const firstCart = carts[0];
          const matchingGroup = availableCartGroups.find(
            (group) => group.catalogItemId === firstCart.catalogItemId
          );

          if (!matchingGroup) {
            const assignedCartNameValue = getAssignedCartName();
            setErrorType('wrong-cart');
            setDetectionState('error-wrong-cart');
            setAssignedCartName(assignedCartNameValue);
            setDetectedItem({
              id: 'CC-WRONG-CART',
              name: 'CC-WRONG-CART',
            });
            return;
          }

          // Find the first unallocated checklist item
          const unallocatedItem = matchingGroup.items.find(
            (item) => !item.allocated
          );

          if (!unallocatedItem) {
            const assignedCartNameValue = getAssignedCartName();
            setErrorType('wrong-cart');
            setDetectionState('error-wrong-cart');
            setAssignedCartName(assignedCartNameValue);
            setDetectedItem({
              id: 'CC-WRONG-CART',
              name: 'CC-WRONG-CART',
            });
            return;
          }

          // Assign the cart via API
          handleCartSelect(unallocatedItem, true);
        }
      } else {
        // For tray phase, just reset detection state
        setDetectionState('info');
      }
    },
    [currentPhase, caseEntity, buildCartEntity, handleCartSelect]
  );

  // Handler for when detection success view completes (after 1 second)
  const handleDetectionSuccessComplete = useCallback(() => {
    // Reset detection state to info to show the instruction view again
    // For cart detection: phase is already 'tray' after cart selection, so just reset state
    // For tray detection: phase stays 'tray', just reset state
    setDetectionState('info');
    setDetectionPhase(BuildCartPhase.CART); // Reset to default, will be set when detection happens
  }, []);

  const clearIdleTimer = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    // Just open the modal - countdown is managed inside SessionEndingDialog
    setShowSessionEndingModal(true);
  }, []);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    if (!session) return;

    idleTimeoutRef.current = setTimeout(() => {
      setShowSessionEndingModal(true);
      startCountdown();
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, session, startCountdown]);

  const handleUserActivity = useCallback(() => {
    // For idle reset only; do not extend session here to avoid noisy calls.
    if (!session || showSessionEndingModal) {
      return;
    }

    startIdleTimer();
  }, [session, showSessionEndingModal, startIdleTimer]);

  const handleContinueEditing = useCallback(async () => {
    if (!caseId) return;
    try {
      await caseScheduleRepository.extendEditingSession(caseId);
      setShowSessionEndingModal(false);
      startIdleTimer();
    } catch (error) {
      console.error('Failed to continue editing session', error);
    }
  }, [caseId, startIdleTimer]);

  const handleIdleLeaveSession = useCallback(async () => {
    setHasSessionTimedOut(true);
    setShowSessionEndingModal(false);
    setShowLeaveModal(false);
    clearIdleTimer();
    // Always reset blocker to prevent it from blocking timeout navigation
    leaveBlocker.reset?.();
    // Mark sidehub as available (fire-and-forget)
    void markSidehubAsAvailable().catch((error) => {
      console.error('Failed to mark sidehub as available after timeout', error);
    });
    // Use setTimeout to ensure state update is processed before navigation
    // This prevents the blocker from intercepting the navigation
    setTimeout(() => {
      invalidateCaseScheduleQueries();
      navigate({ to: '/case-schedule' });
    }, 0);
    // Stop session in background (fire-and-forget)
    void stopSession().catch((error) => {
      console.error('Failed to stop session after timeout', error);
    });
  }, [
    clearIdleTimer,
    navigate,
    stopSession,
    leaveBlocker,
    invalidateCaseScheduleQueries,
    markSidehubAsAvailable,
  ]);

  // Inactivity detection: reset idle timer on user actions, start warning after 4 minutes idle
  useEffect(() => {
    if (!isSessionActive) {
      clearIdleTimer();
      setShowSessionEndingModal(false);
      setHasSessionTimedOut(false);
      return;
    }

    startIdleTimer();

    const activityEvents: (keyof WindowEventMap)[] = [
      'click',
      'keydown',
      'touchstart',
    ];

    const onActivity = () => {
      handleUserActivity();
    };

    activityEvents.forEach((event) =>
      window.addEventListener(event, onActivity)
    );

    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, onActivity)
      );
      clearIdleTimer();
    };
  }, [isSessionActive, clearIdleTimer, startIdleTimer, handleUserActivity]);

  const handleBuildCartSaveAndClose = useCallback(async () => {
    setIsSavingAndClosing(true);
    try {
      await stopSession();
      invalidateCaseScheduleQueries();
      navigate({ to: '/case-schedule' });
    } finally {
      setIsSavingAndClosing(false);
    }
  }, [
    navigate,
    stopSession,
    invalidateCaseScheduleQueries,
  ]);

  // Handler for Remove Cart button click
  const handleRemoveCart = useCallback(() => {
    setShowCartRemovalDialog(true);
  }, []);

  // Handler for Cart Removal Cancel
  const handleCartRemovalCancel = useCallback(() => {
    setShowCartRemovalDialog(false);
  }, []);

  // Handler for Cart Removal Confirm
  const handleCartRemovalConfirm = useCallback(() => {
    // Close instruction dialog
    setShowCartRemovalDialog(false);
    // Show success confirmation
    setShowCartRemovalSuccess(true);

    // Clear any existing timeout
    if (cartRemovalTimeoutRef.current) {
      clearTimeout(cartRemovalTimeoutRef.current);
    }

    // Set timeout for 800ms to auto-dismiss success and reset state
    cartRemovalTimeoutRef.current = setTimeout(() => {
      // Reset detection state
      setDetectionState('info');
      setDetectionPhase(BuildCartPhase.CART);
      setDetectedItem(undefined);
      setErrorMessage(undefined);
      setErrorType(null);
      setAssignedCartName(undefined);

      // Reset buildCartEntity to cart phase
      if (buildCartEntity) {
        const newEntity = new BuildCartEntity({
          caseId: buildCartEntity.caseId,
          currentPhase: BuildCartPhase.CART,
          selectedCartId: undefined,
          selectedCartLocation: undefined,
          selectedCartReferenceNumber: undefined,
          traysLoaded: buildCartEntity.traysLoaded,
          traysRequired: buildCartEntity.traysRequired,
          instrumentsSuppliesLoaded: buildCartEntity.instrumentsSuppliesLoaded,
          instrumentsSuppliesRequired:
            buildCartEntity.instrumentsSuppliesRequired,
        });
        setBuildCartEntity(newEntity);
      }

      // Close success dialog
      setShowCartRemovalSuccess(false);
      cartRemovalTimeoutRef.current = null;
    }, 800);
  }, [buildCartEntity]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (cartRemovalTimeoutRef.current) {
        clearTimeout(cartRemovalTimeoutRef.current);
        cartRemovalTimeoutRef.current = null;
      }
    };
  }, []);

  // Cleanup: mark sidehub as available on component unmount
  useEffect(() => {
    return () => {
      // Fire-and-forget - don't block unmount
      void markSidehubAsAvailable().catch((error) => {
        console.error('Failed to mark sidehub as available on unmount', error);
      });
    };
  }, [markSidehubAsAvailable]);

  // Don't render if session is locked by another user
  if (isLockedByAnotherUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          gap: 2,
        }}
      >
        <Box
          sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'error.main' }}
        >
          Case is Locked
        </Box>
        <Box sx={{ color: 'text.secondary' }}>
          {sessionError ||
            'This case is currently being edited by another user.'}
        </Box>
        <Box sx={{ mt: 2 }}>
          <button onClick={() => navigate({ to: '/case-schedule' })}>
            Back to Case Schedule
          </button>
        </Box>
      </Box>
    );
  }

  // Don't render if there's a session error and no active session
  if (
    sessionError &&
    !session &&
    !isStarting &&
    !isDispatching &&
    !isLeaving &&
    !isSavingAndClosing &&
    !showLeaveModal
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          gap: 2,
        }}
      >
        <Box
          sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'error.main' }}
        >
          Failed to Start Session
        </Box>
        <Box sx={{ color: 'text.secondary' }}>{sessionError}</Box>
        <Box sx={{ mt: 2 }}>
          <button onClick={() => navigate({ to: '/case-schedule' })}>
            Back to Case Schedule
          </button>
        </Box>
      </Box>
    );
  }

  // Don't render if session isn't active yet (still starting or no session)
  if (!session && isStarting && !isDispatching && !isSavingAndClosing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        Starting editing session...
      </Box>
    );
  }

  // Show loading state when saving and closing
  // if (isSavingAndClosing) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
  //       Saving and closing...
  //     </Box>
  //   );
  // }

  // Don't show anything if session timed out - we're navigating away
  if (hasSessionTimedOut) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        Redirecting...
      </Box>
    );
  }

  // Don't render if we don't have an active session
  if (
    !session &&
    !hasSessionTimedOut &&
    !isDispatching &&
    !isLeaving &&
    !isSavingAndClosing &&
    !showLeaveModal
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          gap: 2,
        }}
      >
        <Box
          sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'error.main' }}
        >
          No Active Session
        </Box>
        <Box sx={{ color: 'text.secondary' }}>
          Unable to start editing session. Please try again.
        </Box>
        <Box sx={{ mt: 2 }}>
          <button onClick={() => navigate({ to: '/case-schedule' })}>
            Back to Case Schedule
          </button>
        </Box>
      </Box>
    );
  }

  if (!buildCartEntity || !caseEntity) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        Loading...
      </Box>
    );
  }

  const caseData = {
    id: caseEntity.id,
    caseHospitalId: caseEntity.caseHospitalId,
    surgeon: caseEntity.surgeon,
    formattedStartTime: caseEntity.getFormattedStartTime(),
    caseType: caseEntity.caseType,
    room: caseEntity.room,
    dateTime: caseEntity.dateTime,
    cartCompletenessStatus: caseEntity.cartCompletenessStatus,
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 80px)', // match _auth layout main offset for header
        maxHeight: 'calc(100vh - 80px)',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <BuildCartHeader
        buildCart={buildCartEntity}
        caseData={caseData}
        buildChecklist={caseEntity.buildChecklist}
        onSaveAndClose={handleBuildCartSaveAndClose}
        onNext={handleBuildCartNext}
      />

      {/* Content area */}
      <Box
        sx={{
          padding: '1rem 1rem',
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          backgroundColor: 'white',
        }}
      >
        {caseEntity.buildChecklist && caseEntity.preferenceCard && (
          <>
            {/* Detection Component */}
            <DetectionComponent
              phase={currentPhase}
              state={detectionState}
              detectionPhase={detectionPhase}
              pickingStation={pickingStation}
              onPickingStationEdit={handlePickingStationEdit}
              onDetect={handleDetect}
              detectedItem={detectedItem}
              errorMessage={errorMessage}
              errorType={errorType || undefined}
              assignedCartName={assignedCartName}
              selectedCartId={
                buildCartEntity?.selectedCartId || caseEntity?.cartID
              }
              onOverride={() => {
                // TODO: Implement override logic
                console.log('Override clicked');
              }}
              onRemove={handleRemoveCart}
              onDetectionSuccessComplete={handleDetectionSuccessComplete}
            />

            {/* Item Detection Status Component */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <ItemDetectionStatusComponent
                phase={currentPhase}
                preferenceCard={caseEntity.preferenceCard}
                buildChecklist={caseEntity.buildChecklist}
                onItemClick={handleTraySelect}
                isLoading={
                  currentPhase === BuildCartPhase.CART ? isUpdatingCart : isUpdatingTray
                }
              />
            </Box>
          </>
        )}
      </Box>

      {/* Picking Station Dialog */}
      {pickingStations && showPickingStationDialog && (
        <PickingStationSelectionDialog
          open={showPickingStationDialog}
          stations={pickingStations}
          selectedStationId={selectedPickingStationId}
          onSelectedStationChange={handlePickingStationChange}
          onSelect={handlePickingStationSelect}
          onCancel={handlePickingStationCancel}
        />
      )}

      <SessionEndingDialog
        open={showSessionEndingModal}
        countdownSeconds={WARNING_COUNTDOWN_SECONDS}
        onContinue={handleContinueEditing}
        onLeave={handleIdleLeaveSession}
      />

      <LeaveSessionDialog
        open={showLeaveModal}
        isLeaving={isLeaving}
        onCancel={handleLeaveCancel}
        onConfirm={handleLeaveConfirm}
      />

      <CartRemovalDialog
        open={showCartRemovalDialog}
        onConfirm={handleCartRemovalConfirm}
        onCancel={handleCartRemovalCancel}
      />

      <CartRemovalSuccessDialog open={showCartRemovalSuccess} />
    </Box>
  );
}
